import { useState } from 'react';
import { isEmptyString } from '@/utils/string';
import type { DeliveryFilterValues, DeliverySearchBy } from './types';

/** Parse the comma-separated `fixed` prop into a lookup set of locked filters. */
export function parseFixedFilters(fixed: string | undefined): ReadonlySet<string> {
  if (fixed == null) return new Set();
  return new Set(fixed.split(',').filter((name) => !isEmptyString(name)));
}

/** Whether any of the campaign/action/recipient filters is active (and not fixed). */
function hasQueryFilters(values: DeliveryFilterValues, fixed: ReadonlySet<string>): boolean {
  return (['campaign', 'action', 'recipient'] as const).some((key) => !fixed.has(key) && !isEmptyString(values[key]));
}

interface SearchMode {
  showFilters: boolean;
  searchBy: DeliverySearchBy;
}

/**
 * Derive the filter-row visibility + search mode from the current values, the
 * email-search capability, and the fixed filters. Ported from the Ember
 * `setFilters` method:
 *  - no channel → the filter row is hidden
 *  - a channel → the row shows when email search is available or a filter is
 *    already applied; it defaults to "email" search only when the email channel
 *    already has an active recipient filter, otherwise "filter" search.
 */
function deriveSearchMode(
  values: DeliveryFilterValues,
  isFilteringByEmailEnabled: boolean,
  fixed: ReadonlySet<string>,
): SearchMode {
  if (isEmptyString(values.actionType)) {
    return { showFilters: false, searchBy: 'filter' };
  }
  const queried = hasQueryFilters(values, fixed);
  const emailQueried = queried && !isEmptyString(values.recipient);
  return {
    showFilters: isFilteringByEmailEnabled || queried,
    searchBy: values.actionType === 'email' && emailQueried ? 'email' : 'filter',
  };
}

interface UseSearchModeStateResult extends SearchMode {
  setShowFilters: (next: boolean) => void;
  setSearchBy: (next: DeliverySearchBy) => void;
}

/**
 * Holds the filter-row's local UI state (which is not part of the controlled
 * filter values). Re-derives the mode when the selected channel changes, using
 * React's "adjust state during render on prop change" pattern — no effect, so
 * there's no extra render or stale-flash when the parent updates the channel.
 */
export function useSearchModeState(
  values: DeliveryFilterValues,
  isFilteringByEmailEnabled: boolean,
  fixed: ReadonlySet<string>,
): UseSearchModeStateResult {
  const initial = deriveSearchMode(values, isFilteringByEmailEnabled, fixed);
  const [showFilters, setShowFilters] = useState(initial.showFilters);
  const [searchBy, setSearchBy] = useState<DeliverySearchBy>(initial.searchBy);
  const [prevActionType, setPrevActionType] = useState(values.actionType);

  if (values.actionType !== prevActionType) {
    setPrevActionType(values.actionType);
    const next = deriveSearchMode(values, isFilteringByEmailEnabled, fixed);
    setShowFilters(next.showFilters);
    setSearchBy(next.searchBy);
  }

  return { showFilters, setShowFilters, searchBy, setSearchBy };
}

export interface FilterVisibility {
  hasChannel: boolean;
  isEmailChannel: boolean;
  showStatusFilter: boolean;
  showFindByToggle: boolean;
  showFiltersToggle: boolean;
}

/**
 * Which filter-bar sections are visible, given the values, the fixed
 * (locked/hidden) filters, and whether email search is enabled. Ported from
 * the Ember `showStateFilters` / `showFilterOptions` / `showFiltersToggle`
 * getters. The date-range and channel selects have trivial guards handled
 * inline by the component.
 */
export function getFilterVisibility(
  values: DeliveryFilterValues,
  fixed: ReadonlySet<string>,
  isFilteringByEmailEnabled: boolean,
): FilterVisibility {
  const hasChannel = !isEmptyString(values.actionType);
  const isEmailChannel = values.actionType === 'email';
  const showFindByToggle = isEmailChannel && isFilteringByEmailEnabled;
  const bothMessageFiltersFixed = fixed.has('campaign') && fixed.has('action');
  return {
    hasChannel,
    isEmailChannel,
    showStatusFilter: !fixed.has('deliveryStatus') || !fixed.has('userActions'),
    showFindByToggle,
    showFiltersToggle: hasChannel && !showFindByToggle && !bothMessageFiltersFixed,
  };
}

export interface DeliveryFilterActions {
  onChannelChange: (value: string | null) => void;
  onStatusChange: (value: string | null) => void;
  onCampaignChange: (value: string | null) => void;
  onActionChange: (value: string | null) => void;
  onRecipientChange: (term: string) => void;
  clearFilters: () => void;
  toggleSearchBy: (next: DeliverySearchBy) => void;
  toggleFilters: () => void;
}

interface FilterActionsDeps {
  onChange: (patch: Partial<DeliveryFilterValues>) => void;
  onClearFilters?: () => void;
  showFilters: boolean;
  setShowFilters: (next: boolean) => void;
  setSearchBy: (next: DeliverySearchBy) => void;
}

/**
 * The filter bar's change handlers, ported from the Ember `<cio-deliveries>`
 * action methods. Extracted from the component so the render body stays a thin
 * composition (and below the complexity budget): selecting a campaign clears
 * the dependent action; switching to email search clears the status filter;
 * clearing/toggling resets the campaign/action/recipient trio.
 */
export function useDeliveryFilterActions({
  onChange,
  onClearFilters,
  showFilters,
  setShowFilters,
  setSearchBy,
}: FilterActionsDeps): DeliveryFilterActions {
  const clearFilters = () => {
    onChange({ campaign: null, action: null, recipient: null });
    onClearFilters?.();
  };

  return {
    // Campaign, action, status and recipient are all channel-scoped, so switching
    // channel clears them — otherwise the query runs with a value invalid for the
    // new channel (e.g. an email-only campaign, or a `replied` status on email)
    // and the Select shows a value absent from its options.
    onChannelChange: (value) =>
      onChange({ actionType: value ?? '', campaign: null, action: null, deliveryStatus: null, recipient: null }),
    onStatusChange: (value) => onChange({ deliveryStatus: value ?? null }),
    onCampaignChange: (value) => onChange({ campaign: value ?? null, action: null }),
    onActionChange: (value) => onChange({ action: value ?? null }),
    onRecipientChange: (term) => onChange({ recipient: isEmptyString(term) ? null : term }),
    clearFilters,
    toggleSearchBy: (next) => {
      if (next === 'email') onChange({ deliveryStatus: null });
      clearFilters();
      setSearchBy(next);
    },
    toggleFilters: () => {
      clearFilters();
      setShowFilters(!showFilters);
    },
  };
}
